import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { AiOutlineEdit } from 'react-icons/ai';
import { MdThumbUp, MdThumbDown, MdOutlineCancel } from 'react-icons/md';
import { PiProhibit } from 'react-icons/pi';

import { getInspectionItem, putInspectionItem } from '@/apis/deviceReport';
import type { InspectionItemValue } from '@/types/deviceReport.types';
import type { ErrorResponse } from '@/types/client.types';

import { QUERY_KEY } from '@/constants/queryKey';
import { INPUT_MESSAGE } from '@/constants/constants';

import Modal from '@/components/common/Modal';
import Text from '@/components/common/Text';
import Spacer from '@/components/common/Spacer';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ResultItemValueList from '@/components/work/ResultItemValueList';

import { Theme } from '@/styles/Theme';

import * as S from './style';
import LastEditedHistory from '@/components/inspectionHistory/LastEditedHistory';
import WorkReportPresignedImgList from '@/components/work/WorkReportPresignedImgList';
import { StyledCol, StyledTd } from '@/styles/common';

interface Props {
  itemId: number;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const WorkReportResultItemModal = ({ itemId, setIsModalOpen }: Props) => {
  const location = useLocation();
  const params = useParams();
  const reportId = Number(params.id);

  const queryClient = useQueryClient();

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [result, setResult] = useState<string>('');
  const [resultText, setResultText] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [image, setImage] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string[]>([]);
  const [valueInfo, setValueInfo] = useState<InspectionItemValue[]>([]);
  const [inspectionTime, setInspectionTime] = useState<Date | null>(null);
  const [imageCaptions, setImageCaptions] = useState<string[]>(['', '', '', '', '', '']);

  const { data: itemData } = useQuery(
    [QUERY_KEY.WORK_TARGET_ITEM_RESULT, itemId],
    {
      queryFn: () => getInspectionItem(itemId),
      enabled: !!itemId,
    }
  );

  const { mutate } = useMutation({
    mutationFn: putInspectionItem,
    onSuccess: () => {
      toast.success('수정되었습니다.');
      queryClient.invalidateQueries([
        QUERY_KEY.WORK_TARGET_ITEM_RESULT,
        itemId,
      ]);
      queryClient.invalidateQueries([QUERY_KEY.WORK_TARGET_RESULT, reportId]);
      setIsEdit(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message);
    },
  });

  // edit 페이지에서 모달 키면 edit 모드로
  useEffect(() => {
    if (location.pathname.includes('edit')) {
      setIsEdit(true);
      return;
    }
    setIsEdit(false);
  }, [location.pathname]);

  useEffect(() => {
    if (itemData) {
      const {
        data: { result, result_text, image, selected_image, reason, value, image_captions },
      } = itemData;

      setResult(result ?? '');
      setResultText(result_text ?? '');
      setImage(image ?? []);
      setSelectedImage(selected_image ?? []);
      setReason(isEdit ? '' : reason ?? '');
      setValueInfo(value ?? []);
      
      if (itemData.data.log?.inspectionTime) {
        setInspectionTime(new Date(itemData.data.log.inspectionTime));
      } else if (itemData.data.log?.updatedAt) {
        setInspectionTime(new Date(itemData.data.log.updatedAt));
      } else {
        setInspectionTime(null);
      }
      setImageCaptions(image_captions || ['', '', '', '', '', '']);
    }
  }, [itemData, isEdit]);

  const handleSelect = (selectedResult: string) => {
    if (result === selectedResult) {
      setResult('');
      return;
    }

    setResult(selectedResult);
  };

  const handleRemoveImg = (src: string) => {
    const selectedFiltered = selectedImage.filter((img) => img !== src);
    setSelectedImage(selectedFiltered);
  };

  const handleCaptionChange = (index: number, value: string) => {
    const newCaptions = [...imageCaptions];
    newCaptions[index] = value;
    setImageCaptions(newCaptions);
  };

  const handleConfirmEdit = () => {
    const value = valueInfo.map((info) => ({ [info.template_id]: info.value }));

    // 아무것도 선택 안했을 때
    const mutationData: any = {
      result: result === '-' || result === '' ? '~' : result,
      result_text: resultText,
      image,
      selected_image: selectedImage,
      reason,
      item_id: itemId,
      value,
      image_captions: imageCaptions,
    };

    // Always include inspectionTime if user has selected it
    if (inspectionTime) {
      const originalTime = itemData?.data.log?.inspectionTime || itemData?.data.log?.updatedAt;
      
      console.log('Debug inspectionTime:', {
        originalTime,
        inspectionTime: inspectionTime.toISOString(),
        localTime: inspectionTime.toLocaleString()
      });
      
      // Only send if time has actually changed
      if (originalTime) {
        const originalDate = new Date(originalTime);
        const newDate = new Date(inspectionTime);
        
        // Compare dates with tolerance for timezone differences
        if (Math.abs(originalDate.getTime() - newDate.getTime()) > 1000) { // 1 second tolerance
          mutationData.inspectionTime = inspectionTime.toISOString();
          console.log('Sending inspectionTime:', mutationData.inspectionTime);
        } else {
          console.log('Time not changed, not sending inspectionTime');
        }
      } else {
        // If no original time, always send the new time
        mutationData.inspectionTime = inspectionTime.toISOString();
        console.log('No original time, sending inspectionTime:', mutationData.inspectionTime);
      }
    }

    mutate(mutationData);
  };

  return (
    <Modal
      width='61.875rem'
      closeText='닫기'
      confirmText='수정하기'
      setIsModalOpen={setIsModalOpen}
      hasButton={isEdit}
      onClickConfirm={handleConfirmEdit}
    >
      <S.Container>
        <Spacer y='0.0625rem' />
        <S.TopContainer>
          <S.FlexContainer>
            <S.NumberWrapper>{itemData?.data.seq}</S.NumberWrapper>
            <Text fontSize='1.125rem' fontWeight={700}>
              {itemData?.data.content}
            </Text>
          </S.FlexContainer>
          {isEdit ? (
            <Button height='2.1875rem' onClick={() => setIsEdit(false)}>
              <AiOutlineEdit size='1.25rem' />
              취소하기
            </Button>
          ) : (
            <S.EditContainer>
              {itemData && (
                <LastEditedHistory
                  workerName={itemData?.data.log.workerName}
                  updatedAt={itemData?.data.log.updatedAt}
                  inspectionTime={itemData?.data.log.inspectionTime}
                />
              )}

              <Button height='2.1875rem' onClick={() => setIsEdit(true)}>
                <AiOutlineEdit size='1.25rem' />
                수정하기
              </Button>
            </S.EditContainer>
          )}
        </S.TopContainer>
        {valueInfo.length > 0 && (
          <ResultItemValueList
            isEdit={isEdit}
            valueInfo={valueInfo}
            setValueInfo={setValueInfo}
          />
        )}
        <S.Table>
          <colgroup>
            <StyledCol width='18.75rem' />
            <StyledCol width='18.75rem' />
            <StyledCol width='18.75rem' />
          </colgroup>
          <tbody>
            <tr>
              <td colSpan={3}>점검 사진</td>
            </tr>
            <tr>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[0] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[0]} alt='점검사진_1' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[0])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[1] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[1]} alt='점검사진_2' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[1])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[2] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[2]} alt='점검사진_3' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[2])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
            </tr>
            <tr>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[0] && (
                  <Input
                    name='caption1'
                    value={imageCaptions[0]}
                    onChange={(e) => handleCaptionChange(0, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[1] && (
                  <Input
                    name='caption2'
                    value={imageCaptions[1]}
                    onChange={(e) => handleCaptionChange(1, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[2] && (
                  <Input
                    name='caption3'
                    value={imageCaptions[2]}
                    onChange={(e) => handleCaptionChange(2, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
            </tr>
            <tr>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[3] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[3]} alt='점검사진_4' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[3])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[4] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[4]} alt='점검사진_5' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[4])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
              <StyledTd width='18.75rem' height='18.75rem'>
                {result === '/'
                  ? '해당사항없음'
                  : selectedImage[5] && (
                      <S.ImgContainer>
                        <S.ImgWrapper src={selectedImage[5]} alt='점검사진_6' />
                        {isEdit && (
                          <S.ImgRemoveIconWrapper
                            onClick={() => handleRemoveImg(selectedImage[5])}
                          >
                            <MdOutlineCancel size='1.75rem' />
                          </S.ImgRemoveIconWrapper>
                        )}
                      </S.ImgContainer>
                    )}
              </StyledTd>
            </tr>
            <tr>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[3] && (
                  <Input
                    name='caption4'
                    value={imageCaptions[3]}
                    onChange={(e) => handleCaptionChange(3, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[4] && (
                  <Input
                    name='caption5'
                    value={imageCaptions[4]}
                    onChange={(e) => handleCaptionChange(4, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
              <StyledTd width='18.75rem' height='2.5rem' padding='0.5rem'>
                {selectedImage[5] && (
                  <Input
                    name='caption6'
                    value={imageCaptions[5]}
                    onChange={(e) => handleCaptionChange(5, e.target.value)}
                    width='17.5rem'
                    height='2rem'
                    disabled={!isEdit}
                    placeholder={INPUT_MESSAGE.PHOTO_MEMO.PLACEHOLDER}
                  />
                )}
              </StyledTd>
            </tr>
            <tr>
              <StyledTd
                height='2.5rem'
                colSpan={3}
                textAlign='left'
                padding='0 0 0 0.3125rem'
              >
                <S.FlexContainer>
                  사유 :{' '}
                  {isEdit ? (
                    <S.StyledInput
                      height='2.5rem'
                      value={resultText}
                      onChange={(e) => setResultText(e.target.value)}
                    />
                  ) : (
                    resultText
                  )}
                </S.FlexContainer>
              </StyledTd>
            </tr>
          </tbody>
        </S.Table>
        {!isEdit && (
          <Input
            labelFontSize='1.125rem'
            label='수정 사유'
            name='text'
            width='31.25rem'
            height='2.5rem'
            value={reason}
            disabled
            onChange={(e) => setReason(e.target.value)}
          />
        )}
        {isEdit && (
          <>
            <S.SelectContainer>
              <S.Selector
                backgroundColor={Theme.colors.B_3}
                isSelected={result === 'O'}
                onClick={() => handleSelect('O')}
              >
                <MdThumbUp size='1.5rem' />
                적합
              </S.Selector>
              <S.Selector
                backgroundColor={Theme.colors.WARNING_RED}
                isSelected={result === 'X'}
                onClick={() => handleSelect('X')}
              >
                <MdThumbDown size='1.5rem' />
                미흡
              </S.Selector>
              <S.Selector
                backgroundColor={Theme.colors.G_3}
                isSelected={result === '/'}
                onClick={() => handleSelect('/')}
              >
                <PiProhibit size='1.5rem' />
                해당없음
              </S.Selector>
            </S.SelectContainer>
            {result !== '/' && (
              <WorkReportPresignedImgList
                title='점검 사진 선택'
                selectedImgLength={6}
                isMultiple
                imgList={image}
                setImgList={setImage}
                selectedImgList={selectedImage}
                setSelectedImgList={setSelectedImage}
              />
            )}
            <Input
              labelFontSize='1.125rem'
              label='수정 사유'
              name='text'
              width='31.25rem'
              height='2.5rem'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <S.DatePickerWrapper>
              <Text fontSize='1.125rem' fontWeight={700}>
                점검 시간
              </Text>
              <Spacer y='0.5rem' />
              <DatePicker
                selected={inspectionTime}
                onChange={(date) => setInspectionTime(date)}
                showTimeSelect
                showTimeSelectOnly={false}
                timeFormat="HH:mm:ss"
                timeIntervals={1}
                dateFormat="yyyy-MM-dd HH:mm:ss"
                placeholderText="날짜와 시간을 선택하세요"
                className="date-picker-input"
                isClearable={false}
                autoComplete="off"
                readOnly={false}
              />
            </S.DatePickerWrapper>
          </>
        )}
      </S.Container>
    </Modal>
  );
};

export default WorkReportResultItemModal;
